// traceFileTree.js — pure logic for the X-ray Files tab: reconstruct the
// file tree a scan window's agents touched, from the windows' raw request
// bodies alone.
//
// Request bodies are CUMULATIVE — every request re-carries the whole
// conversation so far, so the same tool call appears in many prompts of a
// session. The pipeline here is: extract per-prompt file operations
// (extractFileOps), then de-duplicate across the window by (session,
// tool_use id) keeping the first sighting (collectWindowOps) — what's left
// is the window's REAL deltas — and fold them into per-endpoint path trees
// with effective-diff stats (buildFileTree).
//
// Classification is by INPUT SHAPE, not tool name (harness-agnostic, same
// rule as the token-efficiency trigger): whole-file `content` ⇒ write;
// old/new string hunks ⇒ edit; a read-named tool with a path ⇒ read.
// Anything else — shell commands, unrecognized tools, edits made outside the
// agent entirely — is invisible here: the tree is a lower bound, and such
// changes surface only when a later read disagrees with the replay (drift).

const READ_TOOLS = new Set(["read", "read_file", "readfile", "notebookread", "view", "open_file"]);

// Write bodies ride into the tab's diff rendering; cap what's retained so a
// generated-artifact write can't balloon the scan state.
const WRITE_CONTENT_CAP = 200_000;

// Read results come back numbered (`N<tab>text` / `N→text`) — free absolute
// line anchors for the reconstruction replay. Cap what one read retains so a
// pathological result can't balloon the scan state (the tool itself caps at
// 2000 lines anyway).
const NUMBERED_OBS_RE = /^\s*(\d+)(?:\t|→)(.*)$/;
const READ_OBS_CAP = 2500;

function parseNumberedObs(text) {
  const obs = [];
  if (!text) return { obs, truncated: false };
  const truncated = /more lines|truncated|content clipped/i.test(text);
  for (const line of text.split(/\r?\n/)) {
    const m = line.match(NUMBERED_OBS_RE);
    if (m) {
      obs.push([parseInt(m[1], 10), m[2]]);
      if (obs.length >= READ_OBS_CAP) break;
    }
  }
  return { obs, truncated };
}

function filePathOf(input) {
  if (!input || typeof input !== "object") return null;
  for (const k of ["file_path", "path", "notebook_path", "filePath"]) {
    const v = input[k];
    if (typeof v === "string" && v) return v;
  }
  return null;
}

function writesFull(input) {
  return typeof input?.content === "string" || typeof input?.file_text === "string";
}

function partialEdits(input) {
  if (!input || typeof input !== "object") return null;
  if (Array.isArray(input.edits)) {
    return input.edits.filter((e) => typeof e?.old_string === "string" || typeof e?.old_str === "string");
  }
  for (const [o, n] of [["old_string", "new_string"], ["old_str", "new_str"]]) {
    if (typeof input[o] === "string") return [{ old_string: input[o], new_string: input[n] ?? "" }];
  }
  if (typeof input.insert_text === "string") return [{ old_string: "", new_string: input.insert_text }];
  return null;
}

function contentText(content) {
  if (typeof content === "string") return content;
  if (Array.isArray(content)) {
    let out = "";
    for (const c of content) {
      if (typeof c === "string") out += c;
      else if (c && typeof c.text === "string") out += c.text;
    }
    return out;
  }
  return "";
}

function resultErrored(result) {
  if (!result) return false;
  if (result.isError) return true;
  return contentText(result.content).includes("<tool_use_error>");
}

// A read whose result came back as an ATTACHMENT rather than text: an image or
// a document, which the agent saw and the trace does not hold. The capture
// parser drops every base64 payload (`strip_attachment_payloads`) and leaves a
// descriptor behind, so what survives is the media type and how big the thing
// was — enough to say what was looked at, never enough to show it.
//
// Without this, such a read lands as `readLines: 0` with no observations, which
// renders identically to a read whose content we simply failed to parse. The
// two are not the same claim and a reader has no way to tell them apart.
const MEDIA_BLOCK_TYPES = new Set(["image", "document"]);

export function mediaOfBlock(b) {
  if (!b || !MEDIA_BLOCK_TYPES.has(b.type)) return null;
  const hasSource = b.source && typeof b.source === "object";
  const source = hasSource ? b.source : {};
  const captured = typeof source.data === "string" && source.data.length > 0;
  // `origin_bytes` counts the base64 STRING the parser removed, not the file:
  // 4 encoded characters per 3 real ones. Reporting it raw overstates every
  // attachment by a third.
  const encoded = typeof source.origin_bytes === "number" && Number.isFinite(source.origin_bytes)
    ? source.origin_bytes
    : null;
  return {
    form: b.type,
    mediaType: typeof source.media_type === "string" ? source.media_type : null,
    bytes: encoded == null ? null : Math.floor((encoded * 3) / 4),
    captured,
    // Positive evidence the payload is gone: a descriptor that holds no data.
    // A block carrying no source at all says nothing either way, and reporting
    // that as an absence would be claiming an observation we never made.
    elided: Boolean(hasSource) && !captured,
  };
}

export function mediaOf(result) {
  const content = result?.content;
  if (!Array.isArray(content)) return null;
  for (const b of content) {
    const media = mediaOfBlock(b);
    if (media) return media;
  }
  return null;
}

/** One clause naming an attachment a read returned: what it was, how big, whether we hold it. */
export function mediaSummary(media) {
  if (!media) return "";
  const size = media.bytes == null
    ? null
    : media.bytes >= 1024
      ? `${(media.bytes / 1024).toFixed(1)} KB`
      : `${media.bytes} bytes`;
  return [media.mediaType || media.form || "attachment", size, media.elided ? "bytes not captured" : null]
    .filter(Boolean)
    .join(" · ");
}

const countLines = (s) => (s === "" ? 0 : s.split("\n").length);

const splitLines = (s) => (s === "" || s == null ? [] : String(s).split("\n"));

// Line-level LCS diff of one hunk, for PR-style rendering: rows tagged
// ctx / del / add, with context lines aligned the way a reviewer expects.
// Hunks are old_string/new_string pairs (small); a quadratic-guard falls
// back to plain del-block + add-block if someone edits a monster.
export function diffLines(oldStr, newStr) {
  const a = splitLines(oldStr);
  const b = splitLines(newStr);
  const m = a.length;
  const n = b.length;
  if (m * n > 400_000) {
    return [...a.map((text) => ({ t: "del", text })), ...b.map((text) => ({ t: "add", text }))];
  }
  const width = n + 1;
  const dp = new Uint32Array((m + 1) * width);
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      dp[i * width + j] = a[i] === b[j]
        ? dp[(i + 1) * width + j + 1] + 1
        : Math.max(dp[(i + 1) * width + j], dp[i * width + j + 1]);
    }
  }
  const rows = [];
  let i = 0;
  let j = 0;
  while (i < m && j < n) {
    if (a[i] === b[j]) { rows.push({ t: "ctx", text: a[i] }); i++; j++; }
    else if (dp[(i + 1) * width + j] >= dp[i * width + j + 1]) rows.push({ t: "del", text: a[i++] });
    else rows.push({ t: "add", text: b[j++] });
  }
  while (i < m) rows.push({ t: "del", text: a[i++] });
  while (j < n) rows.push({ t: "add", text: b[j++] });
  return rows;
}

// Effective ± of one hunk: multiset line diff between the replaced and
// replacement text, so rewording one line of a 6-line hunk scores −1/+1,
// not −6/+6.
export function hunkDelta(oldStr, newStr) {
  const a = String(oldStr ?? "").split("\n");
  const b = String(newStr ?? "").split("\n");
  if (String(oldStr ?? "") === "") a.length = 0;
  if (String(newStr ?? "") === "") b.length = 0;
  const counts = new Map();
  for (const l of a) counts.set(l, (counts.get(l) || 0) + 1);
  let common = 0;
  for (const l of b) {
    const c = counts.get(l) || 0;
    if (c > 0) { common += 1; counts.set(l, c - 1); }
  }
  return { minus: a.length - common, plus: b.length - common };
}

// One prompt's file operations, in body order. Each op:
//   { key, kind: read|edit|write, path, ok, plus, minus, readLines }
// `key` is the tool_use block id (the dedup handle across the session's
// cumulative bodies); blocks without ids fall back to a content hash so
// two genuinely identical calls still collapse.
export function extractFileOps(turns) {
  const ops = [];
  if (!Array.isArray(turns)) return ops;
  const resultById = new Map();
  for (const turn of turns) {
    for (const b of turn?.blocks || []) {
      if (b?.type === "tool_result" && b.tool_use_id) resultById.set(b.tool_use_id, b);
    }
  }
  for (const turn of turns) {
    for (const b of turn?.blocks || []) {
      if (b?.type !== "tool_use") continue;
      const name = String(b.name || "").toLowerCase();
      const input = b.input && typeof b.input === "object" ? b.input : {};
      const key = b.id || `synth:${name}:${hashString(JSON.stringify(input))}`;
      const result = b.id ? resultById.get(b.id) : null;
      const ok = !resultErrored(result);

      const path = filePathOf(input);
      if (!path) continue;

      if (writesFull(input)) {
        const body = typeof input.content === "string" ? input.content : input.file_text;
        const truncated = body.length > WRITE_CONTENT_CAP;
        ops.push({
          key, kind: "write", path, ok, plus: countLines(body), minus: 0, readLines: 0,
          content: truncated ? body.slice(0, WRITE_CONTENT_CAP) : body,
          contentTruncated: truncated,
        });
      } else if (partialEdits(input)) {
        let plus = 0;
        let minus = 0;
        const hunks = [];
        for (const e of partialEdits(input)) {
          const oldStr = String(e.old_string ?? e.old_str ?? "");
          const newStr = String(e.new_string ?? e.new_str ?? "");
          const d = hunkDelta(oldStr, newStr);
          plus += d.plus;
          minus += d.minus;
          hunks.push({ old: oldStr, new: newStr });
        }
        ops.push({ key, kind: "edit", path, ok, plus, minus, readLines: 0, hunks, replaceAll: Boolean(input.replace_all) });
      } else if (READ_TOOLS.has(name)) {
        const { obs, truncated } = result ? parseNumberedObs(contentText(result.content)) : { obs: [], truncated: false };
        // A confident FULL view (starts at 1, contiguous to its last line,
        // no truncation, under the tool's 2000-line cap) also pins the
        // file's length during reconstruction.
        const last = obs.length ? obs[obs.length - 1][0] : 0;
        const obsFull = obs.length > 0 && obs[0][0] === 1 && !truncated && last === obs.length && obs.length < 1990;
        const media = obs.length ? null : mediaOf(result);
        ops.push({
          key, kind: "read", path, ok, plus: 0, minus: 0, readLines: obs.length, obs, obsFull,
          ...(media ? { media } : {}),
        });
      }
    }
  }
  return ops;
}

// djb2 — stable, tiny; only used for synthetic dedup keys.
function hashString(s) {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

// ── Compaction-summarized tool calls ────────────────────────────────────────
//
// Compaction drops tool blocks and re-renders the ones it keeps as PROSE in a
// system turn:
//
//   Called the Read tool with the following input: {"file_path":"/abs/p"}
//   Result of calling the Read tool:
//   1<TAB>first line
//   2<TAB>second line
//
// That numbered body is a real read observation — the same absolute line
// anchors extractFileOps harvests from a live tool_result — so a file whose
// only in-tool sighting was compacted away stays reconstructable. Only READ
// summaries carry a body (the harness re-inlines reads; a dropped write is
// gone), so reads are all this parses.
//
// Held OUT of extractFileOps deliberately. The summarized call already counted
// wherever its live body was captured, and a content-derived synthetic key
// can't dedup against the original tool_use id, so folding these into a
// window's deltas would double-count. They are reconstruction substrate a
// surface opts into, tagged `summarized: true` so consumers can tell them apart.
const SUMMARY_CALL_RE = /Called the ([A-Za-z_][\w.-]*) tool with the following input:[ \t]*(?=\{)/g;
const SUMMARY_RESULT_RE = /^\s*Result of calling the [A-Za-z_][\w.-]* tool:[ \t]*\r?\n/;
const SUMMARY_RESULT_GAP = 200;
const SUMMARY_OPS_CAP = 40;

// The JSON object starting at `open`, string- and escape-aware. Returns the raw
// slice plus the index just past its closing brace, or null when unbalanced.
function jsonObjectAt(text, open) {
  let depth = 0;
  let inStr = false;
  for (let i = open; i < text.length; i++) {
    const c = text[i];
    if (inStr) {
      if (c === "\\") i += 1;
      else if (c === '"') inStr = false;
      continue;
    }
    if (c === '"') inStr = true;
    else if (c === "{") depth += 1;
    else if (c === "}" && --depth === 0) return { raw: text.slice(open, i + 1), end: i + 1 };
  }
  return null;
}

// The run of consecutive numbered observations starting at `from`. `closed` =
// the run ended at a line that isn't numbered, i.e. we saw its real end; a run
// that reaches the block's end could have been clipped by compaction with no
// notice, and must not pin file length.
function numberedRun(text, from) {
  const obs = [];
  let i = from;
  let closed = false;
  let truncated = false;
  while (i < text.length) {
    const nl = text.indexOf("\n", i);
    const stop = nl === -1 ? text.length : nl;
    const line = text.slice(i, stop).replace(/\r$/, "");
    const m = line.match(NUMBERED_OBS_RE);
    if (!m) {
      closed = true;
      truncated = /more lines|truncated|content clipped/i.test(line);
      break;
    }
    obs.push([parseInt(m[1], 10), m[2]]);
    i = stop + 1;
    if (obs.length >= READ_OBS_CAP) { truncated = true; break; }
  }
  return { obs, end: Math.min(i, text.length), closed, truncated };
}

export function extractSummaryFileOps(turns) {
  const ops = [];
  if (!Array.isArray(turns)) return ops;
  for (const turn of turns) {
    for (const b of turn?.blocks || []) {
      if (b?.type !== "text" || typeof b.text !== "string") continue;
      const text = b.text;
      SUMMARY_CALL_RE.lastIndex = 0;
      let m;
      while ((m = SUMMARY_CALL_RE.exec(text))) {
        if (ops.length >= SUMMARY_OPS_CAP) return ops;
        const obj = jsonObjectAt(text, SUMMARY_CALL_RE.lastIndex);
        if (!obj) continue;
        SUMMARY_CALL_RE.lastIndex = obj.end;
        if (!READ_TOOLS.has(m[1].toLowerCase())) continue;
        let input = null;
        try { input = JSON.parse(obj.raw); } catch { continue; }
        const path = filePathOf(input);
        if (!path) continue;
        const rm = SUMMARY_RESULT_RE.exec(text.slice(obj.end, obj.end + SUMMARY_RESULT_GAP));
        if (!rm) continue;
        const run = numberedRun(text, obj.end + rm[0].length);
        if (!run.obs.length) continue;
        SUMMARY_CALL_RE.lastIndex = run.end;
        const last = run.obs[run.obs.length - 1][0];
        ops.push({
          key: `sum:read:${hashString(`${path}|${run.obs[0][1]}|${last}|${run.obs.length}`)}`,
          kind: "read", path, ok: true, plus: 0, minus: 0,
          readLines: run.obs.length, obs: run.obs,
          obsFull: run.closed && !run.truncated && run.obs[0][0] === 1
            && last === run.obs.length && run.obs.length < 1990,
          summarized: true,
        });
      }
    }
  }
  return ops;
}

// Flatten per-prompt extractions into the window's real deltas: prompts are
// walked oldest-first so an op is attributed to the FIRST prompt whose body
// carries it (≈ when it actually ran); later cumulative re-sightings in the
// same session collapse onto it.
export function collectWindowOps(promptEvents) {
  const sorted = [...(promptEvents || [])].sort((a, b) => (tsOf(a.ts) ?? 0) - (tsOf(b.ts) ?? 0));
  const seen = new Set();
  const flat = [];
  for (const p of sorted) {
    for (const op of p.ops || []) {
      const dedupKey = `${p.sessionId || ""}|${op.key}`;
      if (seen.has(dedupKey)) continue;
      seen.add(dedupKey);
      flat.push({
        ...op,
        promptId: p.promptId,
        sessionId: p.sessionId || "",
        endpointId: p.endpointId || "",
        endpointName: p.endpointName || "",
        ts: p.ts ?? null,
      });
    }
  }
  return flat;
}

function tsOf(v) {
  if (v instanceof Date) return v.getTime();
  if (typeof v === "number") return v;
  const p = Date.parse(v);
  return Number.isNaN(p) ? null : p;
}

function normalizeSegments(path) {
  const norm = String(path).replace(/\\/g, "/");
  return norm.split("/").filter((s) => s !== "");
}

// Per-endpoint path trees with per-file effective-diff stats. Node shape:
//   dir:  { name, path, dirs: [...], files: [...] }
//   file: { name, path, reads, edits, writes, plus, minus, errored,
//           sessions, prompts (Set of prompt ids), ops (chronological) }
// Single-child directory chains compress into one "a/b/c" node so the tree
// reads like a PR file list, not a filesystem crawl.
export function buildFileTree(flatOps) {
  const endpoints = new Map();

  for (const op of flatOps || []) {
    if (!op.path) continue;
    let ep = endpoints.get(op.endpointId);
    if (!ep) {
      ep = { id: op.endpointId, label: op.endpointName || shortId(op.endpointId), root: newDir("", ""), files: new Map() };
      endpoints.set(op.endpointId, ep);
    }
    let file = ep.files.get(op.path);
    if (!file) {
      const segs = normalizeSegments(op.path);
      file = {
        name: segs[segs.length - 1] || op.path,
        path: op.path,
        reads: 0, edits: 0, writes: 0, plus: 0, minus: 0, errored: 0,
        sessions: new Set(), prompts: new Set(), ops: [],
      };
      ep.files.set(op.path, file);
      insertFile(ep.root, segs, file);
    }
    file.ops.push({
      promptId: op.promptId, sessionId: op.sessionId, ts: op.ts, kind: op.kind,
      plus: op.plus, minus: op.minus, readLines: op.readLines, ok: op.ok,
      hunks: op.hunks, content: op.content, contentTruncated: op.contentTruncated,
      obs: op.obs, obsFull: op.obsFull, replaceAll: op.replaceAll,
    });
    if (!op.ok) { file.errored += 1; continue; }
    file.sessions.add(op.sessionId);
    file.prompts.add(op.promptId);
    if (op.kind === "read") file.reads += 1;
    else if (op.kind === "edit") { file.edits += 1; file.plus += op.plus; file.minus += op.minus; }
    else if (op.kind === "write") { file.writes += 1; file.plus += op.plus; }
  }

  const out = [];
  for (const ep of endpoints.values()) {
    compressChains(ep.root);
    sortDir(ep.root);
    const files = [...ep.files.values()];
    out.push({
      id: ep.id,
      label: ep.label,
      tree: ep.root,
      stats: {
        files: files.length,
        modified: files.filter((f) => f.edits > 0 || f.writes > 0).length,
        plus: files.reduce((a, f) => a + f.plus, 0),
        minus: files.reduce((a, f) => a + f.minus, 0),
      },
    });
  }
  out.sort((a, b) => b.stats.files - a.stats.files || a.label.localeCompare(b.label));

  return {
    endpoints: out,
    totals: {
      endpoints: out.length,
      files: out.reduce((a, e) => a + e.stats.files, 0),
      modified: out.reduce((a, e) => a + e.stats.modified, 0),
      plus: out.reduce((a, e) => a + e.stats.plus, 0),
      minus: out.reduce((a, e) => a + e.stats.minus, 0),
    },
  };
}

const shortId = (id) => (id ? `${String(id).slice(0, 8)}…` : "unknown endpoint");

function newDir(name, path) {
  return { name, path, dirs: new Map(), files: [] };
}

function insertFile(root, segs, file) {
  let node = root;
  for (let i = 0; i < segs.length - 1; i++) {
    const seg = segs[i];
    let child = node.dirs.get(seg);
    if (!child) {
      child = newDir(seg, node.path ? `${node.path}/${seg}` : seg);
      node.dirs.set(seg, child);
    }
    node = child;
  }
  node.files.push(file);
}

function compressChains(node) {
  for (const child of node.dirs.values()) compressChains(child);
  const merged = new Map();
  for (let child of node.dirs.values()) {
    while (child.dirs.size === 1 && child.files.length === 0) {
      const only = child.dirs.values().next().value;
      only.name = `${child.name}/${only.name}`;
      child = only;
    }
    merged.set(child.name, child);
  }
  node.dirs = merged;
}

function sortDir(node) {
  node.dirs = new Map([...node.dirs.entries()].sort((a, b) => a[0].localeCompare(b[0])));
  node.files.sort((a, b) => a.name.localeCompare(b.name));
  for (const child of node.dirs.values()) sortDir(child);
}

// ── Reconstruction replay (annotated) ───────────────────────────────
//
// Fold a file's window ops (chronological, deduped) into its best-known
// content, tracking PER-LINE provenance: each surviving line carries the
// chain of versions the window saw it in (which op set each text), every
// op that touched/confirmed it, and any lines deleted immediately before
// it (tombstones). Read observations pin absolute lines — a read whose
// text disagrees with the replayed state records a new version (content
// drifted through a channel we don't parse, e.g. shell edits). Writes
// replace the file, chaining versions on lines whose text changed. Edits
// replay as string-replace over the contiguous KNOWN runs, then align the
// affected span line-by-line (diffLines with positional del/add pairing)
// so a reworded line KEEPS its identity and history instead of becoming a
// delete + unrelated insert. An edit whose target was never observed is a
// blind edit — reported, never guessed at.

function newEntry(text, op) {
  return { text, versions: [{ op: op ?? null, text }], touches: op ? [op] : [], born: op ?? null, deletedBefore: [] };
}

function touchEntry(entry, op) {
  if (op && entry.touches[entry.touches.length - 1] !== op) entry.touches.push(op);
}

function setEntryText(entry, text, op) {
  if (entry.text !== text) {
    entry.text = text;
    entry.versions.push({ op: op ?? null, text });
  }
  touchEntry(entry, op);
}

function knownRuns(lines) {
  const keys = [...lines.keys()].sort((a, b) => a - b);
  const runs = [];
  let run = null;
  for (const k of keys) {
    if (run && k === run.end + 1) { run.end = k; run.entries.push(lines.get(k)); }
    else { run = { start: k, end: k, entries: [lines.get(k)] }; runs.push(run); }
  }
  return runs;
}

// Align one edited span line-by-line. Consecutive del/add groups pair
// positionally (del[i] ↔ add[i] = the same line reworded → version chain);
// excess dels become tombstones on the next surviving line, excess adds
// are born lines. Returns the span's new entries + any tombstones left
// dangling past the last emitted entry.
function alignSpan(oldEntries, newTexts, op) {
  const rows = diffLines(oldEntries.map((e) => e.text).join("\n"), newTexts.join("\n"));
  const out = [];
  let pendingTombs = [];
  let oi = 0;
  let dels = [];
  let adds = [];
  const emit = (entry) => {
    if (pendingTombs.length) { entry.deletedBefore.push(...pendingTombs); pendingTombs = []; }
    out.push(entry);
  };
  const flush = () => {
    const n = Math.min(dels.length, adds.length);
    for (let i = 0; i < n; i++) { const e = dels[i]; setEntryText(e, adds[i], op); emit(e); }
    for (let i = n; i < dels.length; i++) pendingTombs.push({ op, text: dels[i].text });
    for (let i = n; i < adds.length; i++) emit(newEntry(adds[i], op));
    dels = [];
    adds = [];
  };
  for (const r of rows) {
    if (r.t === "ctx") {
      flush();
      const e = oldEntries[oi++];
      touchEntry(e, op);
      emit(e);
    } else if (r.t === "del") {
      if (adds.length) flush();
      dels.push(oldEntries[oi++]);
    } else {
      adds.push(r.text);
    }
  }
  flush();
  return { out, pendingTombs };
}

function applyEditOnce(state, oldStr, newStr, op) {
  if (!oldStr) return false;
  const { lines } = state;
  for (const run of knownRuns(lines)) {
    const text = run.entries.map((e) => e.text).join("\n");
    const idx = text.indexOf(oldStr);
    if (idx === -1) continue;
    const startLineInRun = text.slice(0, idx).split("\n").length - 1;
    const endLineInRun = Math.min(run.entries.length - 1, text.slice(0, idx + oldStr.length).split("\n").length - 1);
    const tailCount = run.entries.length - 1 - endLineInRun;
    const newRunLines = (text.slice(0, idx) + newStr + text.slice(idx + oldStr.length)).split("\n");
    const delta = newRunLines.length - run.entries.length;
    const newSpanTexts = newRunLines.slice(startLineInRun, newRunLines.length - tailCount);
    const { out, pendingTombs } = alignSpan(run.entries.slice(startLineInRun, endLineInRun + 1), newSpanTexts, op);
    const tailEntries = run.entries.slice(endLineInRun + 1);
    if (pendingTombs.length) {
      if (tailEntries.length) {
        // A survivor remains in this run right after the deletion.
        tailEntries[0].deletedBefore.push(...pendingTombs);
      } else {
        // The deletion reached this run's end; anchor the tombs before
        // the next KNOWN line (the first entry of the following run, if
        // any), else they trail the whole file.
        let nextKey = Infinity;
        for (const k of lines.keys()) if (k > run.end && k < nextKey) nextKey = k;
        if (nextKey !== Infinity) lines.get(nextKey).deletedBefore.push(...pendingTombs);
        else state.trailingTombs.push(...pendingTombs);
      }
    }
    const after = new Map();
    for (const [k, v] of lines) {
      if (k < run.start) after.set(k, v);
      else if (k > run.end) after.set(k + delta, v);
    }
    run.entries.slice(0, startLineInRun).forEach((e, i) => after.set(run.start + i, e));
    out.forEach((e, i) => after.set(run.start + startLineInRun + i, e));
    tailEntries.forEach((e, i) => after.set(run.start + startLineInRun + out.length + i, e));
    lines.clear();
    for (const [k, v] of after) lines.set(k, v);
    return true;
  }
  return false;
}

export function reconstructFile(ops) {
  const state = { lines: new Map(), trailingTombs: [] };
  const { lines } = state;
  let knownLength = null;
  let blindEdits = 0;
  const blind = [];
  for (const op of ops || []) {
    if (!op.ok) continue;
    if (op.kind === "read" && op.obs?.length) {
      for (const [n, t] of op.obs) {
        const e = lines.get(n);
        if (op.obsTrimmed) {
          // A line seen less its surrounding whitespace fills a line nothing
          // else showed, and is no change to one that agrees with it once
          // that whitespace is set aside. Text that differs still is. An edit
          // keeps the mark: what it changed sits in a line whose surrounding
          // whitespace was still never seen.
          if (!e) lines.set(n, Object.assign(newEntry(t, op), { trimmed: true }));
          else if (e.text.trim() === t.trim()) touchEntry(e, op);
          else { setEntryText(e, t, op); e.trimmed = true; }
        } else if (e?.trimmed && e.text.trim() === t.trim()) {
          // The same line, now seen whole: no new version, only its text.
          e.text = t;
          e.versions[e.versions.length - 1].text = t;
          e.trimmed = false;
          touchEntry(e, op);
        } else if (e) {
          setEntryText(e, t, op);
          e.trimmed = false;
        } else lines.set(n, newEntry(t, op));
      }
      if (op.obsFull) {
        const last = op.obs[op.obs.length - 1][0];
        knownLength = last;
        // Stale tail knowledge, not an observed deletion — drop silently.
        for (const k of [...lines.keys()]) if (k > last) lines.delete(k);
      }
    } else if (op.kind === "write" && typeof op.content === "string") {
      const ls = op.content.split("\n");
      const next = new Map();
      ls.forEach((t, i) => {
        const old = lines.get(i + 1);
        if (old) { setEntryText(old, t, op); old.trimmed = false; next.set(i + 1, old); }
        else next.set(i + 1, newEntry(t, op));
      });
      const removed = [...lines.entries()].filter(([k]) => k > ls.length).sort((a, b) => a[0] - b[0]);
      for (const [, e] of removed) state.trailingTombs.push({ op, text: e.text });
      lines.clear();
      for (const [k, v] of next) lines.set(k, v);
      knownLength = op.contentTruncated ? null : ls.length;
    } else if (op.kind === "edit") {
      const missed = [];
      for (const h of op.hunks || []) {
        let applied = 0;
        if (applyEditOnce(state, h.old, h.new, op)) {
          applied = 1;
          if (op.replaceAll) {
            let guard = 0;
            while (guard++ < 200 && applyEditOnce(state, h.old, h.new, op)) applied += 1;
          }
        }
        if (applied) {
          // Each replacement shifts the file by the hunk's own net line
          // delta, so a multi-occurrence replace_all shifts by delta×count.
          if (knownLength != null) knownLength += applied * (splitLines(h.new).length - splitLines(h.old).length);
        } else {
          blindEdits += 1;
          missed.push(h);
        }
      }
      if (missed.length) blind.push({ op, hunks: missed });
    }
  }
  const keys = [...lines.keys()].sort((a, b) => a - b);
  const total = knownLength ?? (keys.length ? keys[keys.length - 1] : 0);
  const rows = [];
  let i = 1;
  while (i <= total) {
    const entry = lines.get(i);
    if (entry) {
      for (const t of entry.deletedBefore) rows.push({ del: true, text: t.text, op: t.op });
      rows.push({ no: i, text: entry.text, entry });
      i++;
    } else {
      let j = i;
      while (j <= total && !lines.has(j)) j++;
      rows.push({ gap: [i, j - 1] });
      i = j;
    }
  }
  for (const t of state.trailingTombs) rows.push({ del: true, text: t.text, op: t.op });
  return { rows, known: lines.size, total, lengthKnown: knownLength != null, blindEdits, blind };
}

// Classify a reconstructed line's provenance for the gutter, most-salient
// first. "drift" = its latest version came from a READ that disagreed with
// the replayed state — the file changed through a channel we don't parse
// (shell edit, external tool) between an in-tool op and a later read.
// "edited" = an edit changed its text. "inserted"/"written" = the line was
// authored in-window (by an edit hunk / by a whole-file write). "context" =
// an edit or write op spanned the line but left its text unchanged (it was
// the anchor context, not the change). "read" = only ever observed.
export function classifyLine(entry) {
  const vs = entry.versions || [];
  const last = vs[vs.length - 1];
  if (vs.length > 1 && last?.op?.kind === "read") return "drift";
  if (vs.slice(1).some((v) => v.op?.kind === "edit")) return "edited";
  if (entry.born?.kind === "edit") return "inserted";
  if (entry.born?.kind === "write") return "written";
  if ((entry.touches || []).some((op) => op && (op.kind === "edit" || op.kind === "write"))) return "context";
  return "read";
}
